import React, { useEffect, useContext, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import InterviewService from "../../Services/interview.service";
import { DashboardContext } from "../../Context/DashboardContext";
import LoaderContext from "../../Context/LoaderContext/Index";
import { useInterviewRoundContext } from "../../Context/InterviewRoundContext";
import usePollingFunctions from "./hooks/usePollingFunctions";
import useProctoring from "./hooks/useProctoring";
import useRoundValidator from "./hooks/useRoundValidator";
import InterviewRoundPage from "./components/InterviewRoundPage";
import useInterviewEvaluationFlow from "./hooks/useInterviewEvaluationFlow";
import { InterviewSession } from "../../Interface/Index";
import "./index.scss";

const InterviewRounds: React.FC = () => {
  const interviewService = useMemo(() => new InterviewService(), []);

  const { sessionId } = useParams();

  const { setAppliedJob, appliedJob, updateSession, initComplete } = useContext(DashboardContext);
  const { setLoading, setLoadingMessage } = useContext(LoaderContext);

  const { checkForNextRoundQuestions } = usePollingFunctions();

  const { handlePageRefesh } = useProctoring();

  const { checkViolationLimitReached, isUserUsingSameDevice } =
    useRoundValidator();

  const { handleSessionRecovery } = useInterviewEvaluationFlow();

  const {
    setCurrentSession,
    setCurrentSessionQuestions,
    setCurrentSessionQuestionAnswers,
    setIsRoundFinished,
    setActiveQuestionIndex,
  } = useInterviewRoundContext();

  const [pendingRecovery, setPendingRecovery] = useState<{
    sessionId: string;
    session: InterviewSession;
  } | null>(null);

  const [isSameDevice, setIsSameDevice] = useState(true);
  const [terminatedStats, setTerminatedStats] = useState({
    isLimitReached: false,
    violationType: "",
  });

  const handleCompletedSession = async (session: InterviewSession) => {
    console.log("Session is completed");
    const { isLimitReached } = checkViolationLimitReached(session);
    setLoadingMessage("Loading questions, please wait...");
    const questions = await interviewService.getQuestionsBySessionId(
      session._id
    );
    if (!questions || questions.length === 0) {
      console.error("No questions found for the session");
      return;
    }
    const answers = questions.map((q: any) => ({
      questionId: q._id,
      answer: q.responses?.[0]?.description ?? null,
      fileUrl:
        q.type === "fileUpload" ? q.responses?.[0]?.fileUrl ?? null : null,
    }));
    setCurrentSession(session);
    setCurrentSessionQuestions(questions);
    setCurrentSessionQuestionAnswers(answers);
    setActiveQuestionIndex(isLimitReached ? -1 : questions.length);
    setLoading(false);
    setLoadingMessage(null);
    setIsRoundFinished(true);
  };

  const handlePendingSession = (session: InterviewSession) => {
    console.log("Session is pending");
    setCurrentSession(session);
    setIsRoundFinished(false);
    setActiveQuestionIndex(-1);
    setLoading(false);
    setLoadingMessage(null);
  };

  const handleDifferentDeviceDetection = (session: InterviewSession) => {
    console.log("Different device detected");
    setCurrentSession(session);
    setActiveQuestionIndex(-1);
    setLoading(false);
    setLoadingMessage(null);
  };

  const handleRefreshDetection = async (session: InterviewSession) => {
    console.log("Refresh attempt detected");
    const response = await handlePageRefesh(session);
    if (response) {
      setCurrentSession(response.interviewSession);
      updateSession(response.interviewSession._id, response.interviewSession);
      setAppliedJob(response.jobApplication);
    }
    setActiveQuestionIndex(-1);
    setLoading(false);
    setLoadingMessage(null);
  };

  const initInterviewRound = async (sessionId: string) => {
    console.log("Init called");
    setLoading(true);
    setLoadingMessage("Loading interview round, Please wait...");

    let session = await interviewService.findSessionById(sessionId);
    if (!session) {
      console.log("Session not found");
      setLoading(false);
      setLoadingMessage(null);
      return;
    }

    if (!session?.isQuestionsGenerated) {
      setLoadingMessage("Generating questions, please wait...");
      const updatedSession = await checkForNextRoundQuestions(session._id);
      session = updatedSession;
      updateSession(updatedSession._id, updatedSession);
    }

    // Detecting page refresh during session evaluation
    const isRefreshed = localStorage.getItem("refresh") === "true";
    const refreshedSessionId = localStorage.getItem("refreshedSessionId");
    if (
      isRefreshed &&
      refreshedSessionId &&
      session._id === refreshedSessionId
    ) {
      console.log(
        "Session was refreshed during evaluation, handling recovery..."
      );
      setLoadingMessage("Recovering session, Please wait...");
      setPendingRecovery({ sessionId: refreshedSessionId, session });
      return;
    }

    setLoadingMessage("Almost there... just wrapping things up!");

    const isLive = session?.isLive;
    const isCompleted = session?.status !== "pending";
    const isSameDevice = await isUserUsingSameDevice(session);

    setIsSameDevice(isSameDevice);
    setTerminatedStats(checkViolationLimitReached(session));

    // case:1 round is completed
    if (isCompleted) {
      return await handleCompletedSession(session);
    }

    // case:2 round is pending
    if (!isLive && session?.status === "pending" && !session.sessionToken) {
      return handlePendingSession(session);
    }

    // case:3 session is live and using different device
    if (isLive && !isSameDevice && session.status === "pending") {
      return handleDifferentDeviceDetection(session);
    }

    // case:4 session is live same device (refresh attempt)
    if (isLive && isSameDevice && session.status === "pending") {
      return await handleRefreshDetection(session);
    }
  };

  useEffect(() => {
    if (pendingRecovery && initComplete && appliedJob) {
      handleSessionRecovery(pendingRecovery.sessionId, pendingRecovery.session);
      setPendingRecovery(null);
    }
  }, [pendingRecovery, initComplete, appliedJob]);

  useEffect(() => {
    if (!sessionId) {
      console.log("SessionId is not present in the url");
      return;
    }
    if (initComplete) {
      initInterviewRound(sessionId);
    } else {
      console.log("Dashboard initialization is not done");
    }
  }, [sessionId, initComplete]);

  return (
    <InterviewRoundPage
      isSameDevice={isSameDevice}
      terminatedStats={terminatedStats}
    />
  );
};

export default InterviewRounds;
